// client/src/App.js
import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import DashboardPage from './pages/DashboardPage';
import DealerDashboardPage from './pages/DealerDashboardPage';
import OrdersPage from './pages/OrdersPage';
import DealersPage from './pages/DealersPage';
import SettingsPage from './pages/SettingsPage';
import AccountsPage from './pages/AccountsPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ReportsPage from './pages/ReportsPage';
import { useAuth } from './context/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './App.css';

// ProtectedRoute with role enforcement 
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, user, setAuthPageMessage } = useAuth();

  // Clear any authentication-related messages when a protected route is accessed
  useEffect(() => {
    setAuthPageMessage('', '');
  }, [setAuthPageMessage]);

  // Not logged in? -> login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Role not allowed? -> send to correct home for their role
  if (allowedRoles.length > 0 && (!user || !allowedRoles.includes(user.role))) {
    setAuthPageMessage('You do not have permission to access this page.', 'danger');

    // Auto-clear after 3 seconds
    setTimeout(() => {
      setAuthPageMessage('', '');
    }, 3000);

    const fallback = user?.role === 'Dealer' ? '/dealer-dashboard' : '/dashboard';
    return <Navigate to={fallback} replace />;
  }

  return children;
};


// Helper: send authed users to their home route based on role
function RoleHomeRedirect() {
  const { user } = useAuth();
  if (user?.role === 'Dealer') return <Navigate to="/dealer-dashboard" replace />;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="App d-flex flex-column min-vh-100">
      {/* Header only when authenticated */}
      {isAuthenticated && <Header />}

      <main className="flex-grow-1">
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

          {/* Dealer-only */}
          <Route
            path="/dealer-dashboard"
            element={
              <ProtectedRoute allowedRoles={['Dealer']}>
                <DealerDashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Settings: allowed to all authenticated roles (including Dealer) */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedRoles={['Dealer', 'Admin', 'PreProduction', 'Manufacturing', 'Shipping', 'Employee', 'Supervisor']}>
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          {/* Non-dealer app pages */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRoles={['Admin', 'PreProduction', 'Manufacturing', 'Shipping', 'Employee', 'Supervisor']}>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute allowedRoles={['Admin', 'PreProduction', 'Manufacturing', 'Shipping']}>
                <OrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute allowedRoles={['Admin', 'PreProduction', 'Manufacturing', 'Shipping']}>
                <ReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dealers"
            element={
              <ProtectedRoute allowedRoles={['Admin', 'PreProduction', 'Manufacturing', 'Shipping']}>
                <DealersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts"
            element={
              <ProtectedRoute allowedRoles={['Admin', 'PreProduction', 'Manufacturing', 'Shipping']}>
                <AccountsPage />
              </ProtectedRoute>
            }
          />

          {/* Default route sends authed users to their proper dashboard */}
          <Route
            path="/"
            element={
              <ProtectedRoute allowedRoles={['Dealer', 'Admin', 'PreProduction', 'Manufacturing', 'Shipping', 'Employee', 'Supervisor']}>
                <RoleHomeRedirect />
              </ProtectedRoute>
            }
          />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to={isAuthenticated ? '/' : '/login'} replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default App;
